import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CssBaseline, useTheme, useMediaQuery } from '@mui/material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

// Layout components
import Header from './components/layout/Header';
import Sidebar from './components/layout/Sidebar';
// Event service
import { eventService, EventType } from './services/eventService';

// Pages
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Setup from './pages/Setup';
import Dashboard from './pages/Dashboard';
import SensorList from './pages/sensors/SensorList';
import SensorDetail from './pages/sensors/SensorDetail';
import SensorMap from './pages/sensors/SensorMap';
import AlertList from './pages/alerts/AlertList';
import AlertDetail from './pages/alerts/AlertDetail';
import SuspensionList from './pages/suspensions/SuspensionList';
import UserList from './pages/users/UserList';
import NotFound from './pages/NotFound';
import Settings from './pages/Settings';
import IngecomSettings from './pages/IngecomSettings';
import Profile from './pages/Profile';
import TestDevice from './pages/TestDevice';

// Contexts
import { useAuth } from './contexts/AuthContext';

// WebSocket connection
import { initWebSocketConnection } from './services/websocketService';

const App: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  
  // Handle sidebar toggle
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Initialize WebSocket connection when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const wsCleanup = initWebSocketConnection({
        onAlert: (alertData) => {
          // Show toast notification
          toast.info(`${t('common.newAlert')} ${alertData.sensor_name}: ${t(`eventTypes.${alertData.event_type}`)}`, {
            onClick: () => {
              window.location.href = `/alerts/${alertData.id}`;
            },
          });
          
          // Broadcast event to all components
          eventService.emit(EventType.NEW_ALERT, alertData);
        },
        onSensorUpdate: (sensorData) => {
          // Broadcast sensor update event to all components
          eventService.emit(EventType.SENSOR_UPDATE, sensorData);
        },
        onError: (error) => {
          console.error('WebSocket error:', error);
          toast.error(t('common.connectionLost'));
        },
        onReconnect: () => {
          toast.success(t('common.reconnected'));
        },
      });

      return () => {
        wsCleanup();
      };
    }
  }, [isAuthenticated, t]);

  // Root level application with routes
  return (
    <Routes>
      {/* Public setup route - accessible without auth */}
      <Route path="/setup" element={
        <Box sx={{ display: 'flex', height: '100vh' }}>
          <CssBaseline />
          <Setup />
        </Box>
      } />

      {/* Unauthenticated routes */}
      <Route path="/login" element={
        !isAuthenticated ? (
          <Box sx={{ display: 'flex', height: '100vh' }}>
            <CssBaseline />
            <Login />
          </Box>
        ) : (
          <Navigate to="/" replace />
        )
      } />

      <Route path="/forgot-password" element={
        !isAuthenticated ? (
          <Box sx={{ display: 'flex', height: '100vh' }}>
            <CssBaseline />
            <ForgotPassword />
          </Box>
        ) : (
          <Navigate to="/" replace />
        )
      } />

      <Route path="/reset-password" element={
        !isAuthenticated ? (
          <Box sx={{ display: 'flex', height: '100vh' }}>
            <CssBaseline />
            <ResetPassword />
          </Box>
        ) : (
          <Navigate to="/" replace />
        )
      } />


      {/* Redirect all other unauthenticated paths to login */}
      <Route path="/*" element={
        !isAuthenticated ? (
          <Navigate to="/login" replace />
        ) : (
          // Main authenticated application layout
          <Box sx={{ display: 'flex', height: '100vh' }}>
            <CssBaseline />
            <Header toggleSidebar={toggleSidebar} />
            <Sidebar open={sidebarOpen} toggleSidebar={toggleSidebar} />
            <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8, overflow: 'auto' }}>
              <Routes>
                <Route
                  path="/"
                  element={<Dashboard />}
                />
                <Route
                  path="/sensors"
                  element={<SensorList />}
                />
                <Route
                  path="/sensors/:id"
                  element={<SensorDetail />}
                />
                <Route
                  path="/test/:sensorId"
                  element={<TestDevice />}
                />
                <Route
                  path="/map"
                  element={<SensorMap />}
                />
                <Route
                  path="/alerts"
                  element={<AlertList />}
                />
                <Route
                  path="/alerts/:id"
                  element={<AlertDetail />}
                />
                <Route
                  path="/suspensions"
                  element={<SuspensionList />}
                />
                <Route
                  path="/users"
                  element={<UserList />}
                />
                <Route
                  path="/settings"
                  element={<Settings />}
                />
                <Route
                  path="/ingecom-settings"
                  element={<IngecomSettings />}
                />
                <Route
                  path="/profile"
                  element={<Profile />}
                />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Box>
          </Box>
        )
      } />
    </Routes>
  );
};

export default App;